import { APIClient, APIResponseError } from '@cmdshepard/api-client';
import { createParser } from 'eventsource-parser';
import {
  ASSESSMENT_SESSION_TOKEN_HEADER,
  StreamEventType,
  type AssessmentResponse,
  type CompetencySlug,
  type QuestionType,
  type StreamEvent,
} from '@evaltick/shared';
import { isGuestToken, stripGuestTokenPrefix } from '@/utils/guestSession';

export { APIResponseError };

export interface CoachRequest {
  message: string;
  competencyId: CompetencySlug;
  questionType: QuestionType;
  threadId?: string;
  questionText?: string;
}

export interface CoachStreamHandlers {
  onChunk?: (text: string) => void;
  onError?: (error: Error) => void;
  onComplete?: (threadId: string) => void;
}

export function parseErrorMsg(e: unknown, fallback = 'Something went wrong'): string {
  if (e instanceof APIResponseError) {
    if (e.body?.message) return e.body.message;
    if (typeof e.body === 'string' && e.body) return e.body;
    return `${e.status} ${e.statusText}`;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

const EVALUATE_URL = '/agents/evaluate';
const CLARIFY_URL = '/agents/chat/stream-nodes';

class BaseAPIClient {
  protected readonly client: APIClient;
  protected readonly fetchClient: (url: string, options?: RequestInit) => Promise<Response>;
  protected readonly token: string;

  constructor(token: string) {
    this.token = token;
    // A guest session token (see utils/guestSession.ts) goes on its own header
    // instead of Authorization, matching the backend's authenticateSessionToken.
    const authHeaders: Record<string, string> = isGuestToken(token)
      ? { [ASSESSMENT_SESSION_TOKEN_HEADER]: stripGuestTokenPrefix(token) }
      : { Authorization: `Bearer ${token}` };

    this.client = new APIClient({
      host: import.meta.env.VITE_SERVER_URL,
      contentType: APIClient.CONTENT_TYPE.JSON,
      headers: authHeaders,
    });

    this.fetchClient = async (url: string, options: RequestInit = {}) => {
      const defaultHeaders = {
        'Content-Type': 'application/json',
        ...authHeaders,
      };

      return fetch(import.meta.env.VITE_SERVER_URL + url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...(options.headers || {}),
        },
      });
    };
  }

  protected async request<T>(call: () => Promise<T>): Promise<T> {
    try {
      return await call();
    } catch (e) {
      if (e instanceof APIResponseError) throw e;
      throw new Error(parseErrorMsg(e));
    }
  }

  async evaluate(request: CoachRequest, handlers: CoachStreamHandlers) {
    await this.streamCoach(EVALUATE_URL, request, handlers);
  }

  async clarify(request: CoachRequest, handlers: CoachStreamHandlers) {
    await this.streamCoach(CLARIFY_URL, request, handlers);
  }

  async getAssessmentResponse(threadId: string): Promise<AssessmentResponse> {
    return this.client.get(`/agents/response/${threadId}`);
  }

  private async streamCoach(
    url: string,
    request: CoachRequest,
    handlers: CoachStreamHandlers
  ) {
    try {
      const response = await this.fetchClient(url, {
        method: 'POST',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body available for streaming.');
      }

      const decoder = new TextDecoder();
      let isComplete = false;

      const parser = createParser({
        onEvent: (event) => {
          if (!event.data || event.data.trim() === '') {
            return;
          }

          let parsed: StreamEvent;
          try {
            parsed = JSON.parse(event.data) as StreamEvent;
          } catch {
            handlers.onError?.(
              new Error(`Failed to parse SSE event: ${event.data?.slice(0, 100)}`)
            );
            return;
          }

          switch (parsed.type) {
            case StreamEventType.Chunk:
              handlers.onChunk?.(parsed.text);
              break;
            case StreamEventType.Complete:
              isComplete = true;
              handlers.onComplete?.(parsed.threadId);
              break;
            case StreamEventType.Error:
              isComplete = true;
              handlers.onError?.(new Error(parsed.error));
              break;
          }
        },
      });

      try {
        while (!isComplete) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          parser.feed(decoder.decode(value, { stream: true }));
        }
      } finally {
        reader.cancel();
      }

      if (!isComplete) {
        handlers.onError?.(new Error('Stream closed without a complete event'));
      }
    } catch (error) {
      handlers.onError?.(error instanceof Error ? error : new Error('Unknown streaming error'));
    }
  }
}

export default BaseAPIClient;
